import { getQueryKey, queryFn } from '../query';
import { useQuery } from '@tanstack/react-query';
import { getDefaultPeer, mapCompanyToPeer } from '../../../utils';
import { useMemo } from 'react';
import {
  getHistogramQueryKey,
  getPeersQueryKey,
  queryHistogramFn,
  queryPeersFn,
} from '../../../query';
import {
  getQueryKey as getIndustryAvgQueryKey,
  queryFn as industryAvgQueryFn,
} from '../../../industryAverageQuery';
import type { PreferredMultiple } from '../../../selector';
import { PEERS_LIMIT } from '@/pages/CompanyReport/constants';

const getDefault = () => ({
  median: null,
  maxCount: 0,
  max: 20,
  bucketSize: 1,
  count: 0,
  companies: new Array(20).map((_, i) => ({
    key: `${i}`,
    from: i * 5,
    to: (i + 1) * 5,
    count: 0,
  })),
});

type UseIndustryCompaniesProps = {
  index: number;
  limit: number;
  selectedMultiple?: PreferredMultiple;
  tickerSymbol: string;
  uniqueSymbol: string;
  canonicalUrl?: string;
  multiples: {
    [key: string]: {
      name: PreferredMultiple;
      fullName: string;
      value: string;
      valueRaw: number;
    };
  };
};
export const useIndustryCompanies = ({
  index,
  limit,
  selectedMultiple,
  tickerSymbol,
  uniqueSymbol,
  canonicalUrl,
  multiples,
}: UseIndustryCompaniesProps) => {
  const LAST_BUCKET_INDEX = 20;
  const preferredMultiple = multiples.preferredMultiple.name;
  const preferredMultipleValue = multiples.preferredMultiple.valueRaw;
  const multiple = selectedMultiple || preferredMultiple;
  const multipleValue = Object.values(multiples).find(
    (m) => m.name === multiple
  )?.valueRaw as number;

  //Fetches industry Avg data from API so it is consistent with avg in statements
  const industryAvgData = useQuery({
    queryKey: getIndustryAvgQueryKey(uniqueSymbol),
    queryFn: industryAvgQueryFn,
    staleTime: Infinity,
    enabled: Boolean(uniqueSymbol),
  });

  //Fetch histograms from graphql
  const { data: companyHistogramData, status: companyHistogramStatus } =
    useQuery({
      queryKey: getHistogramQueryKey(canonicalUrl, multiple),
      queryFn: queryHistogramFn,
      enabled: Boolean(canonicalUrl),
    });

  //Fetch company data from graphql
  const { data: companyPeersData } = useQuery({
    queryKey: getPeersQueryKey(canonicalUrl, PEERS_LIMIT),
    queryFn: queryPeersFn,
    staleTime: Infinity,
    enabled: Boolean(canonicalUrl),
  });

  const { company, region, countryISO, industryId, bucketSize } =
    useMemo(() => {
      if (!companyHistogramData || !companyPeersData)
        return {
          bucketSize: 1,
          company: getDefaultPeer(),
          industry: {
            name: '',
            ratioFormatted: 'n/a',
            salesGrowthFormatted: 'n/a',
            earningsGrowthFormatted: 'n/a',
          },
          industryId: 0,
          region: null,
          countryISO: null,
        };
      const { industryHistogram } = companyHistogramData.Company;
      const { secondaryIndustryId, level, bucketSize } = industryHistogram;
      const company = mapCompanyToPeer(multiple)(companyPeersData.Company);

      //For query filter
      const region = level === 'region' ? industryHistogram.region : null;
      const countryISO =
        level === 'country' ? industryHistogram.countryISO : null;

      return {
        company,
        region,
        countryISO,
        bucketSize,
        industryId: secondaryIndustryId,
      };
    }, [companyHistogramData, multiple, companyPeersData]);

  const companyIndex = useMemo(() => {
    if (!multipleValue || !companyHistogramData) return -1;
    const buckets =
      companyHistogramData?.Company.industryHistogram.buckets || [];
    const foundIndex = buckets.findIndex((bucket) => {
      //Bucket to would be null on last bucket
      return bucket.to
        ? multipleValue >= (bucket.from || 0) && multipleValue < bucket.to
        : true;
    });
    return foundIndex;
  }, [companyHistogramData, multipleValue]);

  const min = index * bucketSize;
  const max = index === LAST_BUCKET_INDEX ? Infinity : (index + 1) * bucketSize;
  const isCompanyInIndex = companyIndex === index;

  //Fetch companies within the selected bucket for vs industry chart via graphql
  const { data: companiesData } = useQuery({
    queryKey: getQueryKey(
      multiple,
      countryISO,
      region,
      min,
      max,
      industryId,
      isCompanyInIndex ? limit + 1 : limit //Fetch 1 more in case company will be filtered out from result
    ),
    queryFn,
    staleTime: Infinity,
    enabled: Boolean(industryId && limit),
  });

  const companies = useMemo(
    () =>
      companiesData
        ?.map(mapCompanyToPeer(multiple))
        //Filter so that the selected company is not duplicated (even if secondary listing)
        .filter((c) => c.tickerSymbol !== tickerSymbol)
        .slice(0, limit),
    [limit, companiesData, multiple, tickerSymbol]
  );

  const industryCompanies = useMemo(() => {
    const histogramData = companyHistogramData?.Company.industryHistogram;
    if (!companyHistogramData || !histogramData || !companyPeersData)
      return getDefault();
    let maxCount = 0;
    histogramData.buckets.forEach((bucket, i) => {
      //Exclude last bucket from max
      if (i === histogramData.buckets.length - 1) return;
      if (bucket.count > maxCount) maxCount = bucket.count;
    });

    //Industry Avg value on chart comes from API, so its consistent with the statements data
    const industryAvg = industryAvgData.data?.data?.company.data.find((r) => {
      const name =
        multiple === 'PE' ? 'pe' : multiple === 'PB' ? 'pb' : 'price_to_sales';
      return r.name === name;
    }) || { value: null };

    //Figure out if company is a secondary listing
    //We will need to re-add +1 count to its bucket
    const isSecondarylisting =
      companyPeersData.Company.isPrimaryListing === false;

    const median =
      typeof industryAvg?.value === 'number' ? industryAvg.value : null;
    return {
      median,
      maxCount,
      count: histogramData.count,
      max: histogramData.maxRange,
      bucketSize: histogramData.bucketSize,
      companies: histogramData.buckets.map((bucket, i) => ({
        key: bucket.key,
        from: bucket.from || histogramData.minRange,
        to: bucket.to || histogramData.maxRange,
        count:
          isSecondarylisting && i === companyIndex
            ? bucket.count + 1
            : bucket.count,
      })),
    };
  }, [
    companyHistogramData,
    industryAvgData.data,
    multiple,
    companyPeersData,
    companyIndex,
  ]);
  return {
    company,
    companies,
    companyIndex,
    isCompanyInIndex,
    industryCompanies,
    preferredMultiple,
    preferredMultipleValue,
    multipleValue,
    companyHistogramStatus,
  };
};
